/**
 * Pure builder that maps a resolved form configuration into the context
 * field-value structure consumed by the medical PA form fields.
 *
 * @module medicalPaForm/components/utils/buildFormFieldsData
 */
import { MedicalPaOrder } from "../../../../data-model/medicalPaOrdersModel";
import { FormDataModel } from "../../../../data-model/pharmaPaFormModel";
import { getNestedValue } from "../../../../utils/getNestedValue";
import { extractFieldMappings } from "../../../pharmaPaForm/utils/getFormFieldsInfo";

/**
 * Builds the `formFieldsData` map: for each configured field it resolves the
 * filled value from the pristine order (or the working copy when the form is
 * dirty) and carries validation metadata.
 *
 * @param formConfig - Resolved form configuration.
 * @param isFormDirty - Whether the form has unsaved edits.
 * @param originalMedicalPaOrder - Pristine order used before edits.
 * @param localMedicalPaOrder - Working order copy used once dirty.
 * @returns The field-value map keyed by field path.
 */
export const buildFormFieldsData = (
  formConfig: FormDataModel | null,
  isFormDirty: boolean,
  originalMedicalPaOrder: MedicalPaOrder | null,
  localMedicalPaOrder: MedicalPaOrder | null,
): Record<string, any> => {
  const fieldMappings = extractFieldMappings(formConfig?.fields ?? []);
  return Object.entries(fieldMappings).reduce<Record<string, any>>(
    (acc, [key, value]) => ({
      ...acc,
      [key]: {
        ...(acc[key] || {
          isRequired: value?.isRequired ?? false,
          regexMatcher: value?.regexMatcher ?? null,
          type: value?.type ?? "",
        }),
        filledValue: !isFormDirty
          ? getNestedValue(originalMedicalPaOrder, key, value?.default)
          : getNestedValue(localMedicalPaOrder, key, value?.default),
        isFieldDirty: isFormDirty ?? false,
        default: value?.default ?? null,
      },
    }),
    {},
  );
};
